import React from 'react';

import { Button } from '@/primitives/Button';
import { ChevronLeft } from '@/primitives/Icons';

import styles from './Carousel.module.css';

type CarouselItem = {
  id: string;
  imagePath: string;
};

export const Carousel = ({
  width = 256,
  height = 256,
  items,
  initialIndex = 0,
}: {
  width: number;
  height: number;
  items: CarouselItem[];
  initialIndex?: number;
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const carouselRef = React.useRef<HTMLDivElement>(null);

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, items.length - 1));
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious();
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      handleNext();
      e.preventDefault();
    }
  };

  const currentItem = items[currentIndex];

  return (
    <div
      className={styles.carouselWrapper}
      style={{
        alignSelf: 'center',
        outline: 'none',
      }}
      ref={carouselRef}
      onKeyDown={handleKeyDown}
      aria-roledescription="carousel"
      aria-label="Image carousel"
      aria-live="polite"
      tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      role="group"
    >
      <div className={styles.carousel}>
        <Button
          size="xs"
          icon={<ChevronLeft />}
          tabIndex={0}
          aria-label="previous"
          onClick={handlePrevious}
        />
        <div
          className={styles.imageWrapper}
          style={{ width: `${width}px`, height: `${height}px` }}
        >
          <img
            src={currentItem?.imagePath}
            alt={`System preview ${currentIndex + 1} of ${items.length}`}
          />
        </div>
        <Button
          size="xs"
          icon={<ChevronLeft />}
          tabIndex={0}
          aria-label="next"
          onClick={handleNext}
        />
      </div>
      <div className={styles.indicator}>
        {items.map((item, index) => (
          <div
            key={item.id}
            className={styles.indicatorItem}
            style={{
              backgroundColor:
                index === currentIndex ? 'var(--gray-9)' : 'var(--gray-5)',
            }}
            role="button"
          />
        ))}
      </div>
    </div>
  );
};
